// 브라우저 계정 클라이언트(D5a): 세션 확인 → 주기 동기화 → Google 연결·연결 해제·계정 삭제. 판정은 sync.ts, 이 파일은 fetch·타이머·상태.
// 게임 루프 밖(타이머·화면 이벤트)에서만 돈다. 어떤 실패도 게임 진행을 막지 않고 상태 줄(status)에만 남는다.
import { SyncEngine, TransportError, freshMeta, saveMeta, stageWipe, type SyncOutcome, type Transport } from './sync';
import { type KV } from './keys';
import { CharacterStorage, charSyncKeys, isInstalling } from '../characters/keys';
import { activeCharacterId } from '../characters/scope';
import { characterTransport, fetchCharacterApi } from '../characters/api';
import { charactersEnabled, ensureRegistered, retainLocalCharacters } from '../characters/index';
import { browserLocks, runOnlineMigration, type OnlineMigrationResult } from '../characters/online';
import { googleSignIn } from './google';
import type { AccountView, ConflictResponse, GoogleResponse, PutResponse, SaveResponse, SessionResponse, Snapshot } from './protocol';

export type AccountState =
  | 'off' // 서버 저장 꺼짐(계정 삭제 뒤 로컬 유지)
  | 'connecting' // 세션 확인 중
  | 'syncing'
  | 'synced'
  | 'pending' // 던전 중이라 서버 진행 불러오기를 마을로 미룸
  | 'offline'
  | 'too-large'
  | 'relink' // Google 계정 세션이 사라짐(다른 기기에서 삭제 등): 다시 연결하거나 새로 켜기
  | 'error';
export interface AccountStatus {
  state: AccountState;
  provider: 'guest' | 'google' | null;
  accountId: string | null;
  lastSyncAt: number;
  /** 사용자에게 보일 한 줄(상태 이유·실패 사유) */
  note: string;
}
export interface AccountHooks {
  /** 지금 서버 진행을 불러와(새로고침) 되는 장면인가(타이틀·마을, 결과·던전·일시정지 아님) */
  isSafe(): boolean;
  /** 새로고침 전에 게임의 미저장 진행을 로컬에 쓴다 */
  flush(): void;
  reload(): void;
  /**
   * 캐릭터 이관이 도는 동안 짧게 플레이를 막는다(던전 입장 차단·로딩 표시 — 이관 중 원래 키에 쌓인 진행이 캐릭터로 넘어가지 않는 일을 막는다).
   * 없으면 아무것도 하지 않는다
   */
  setBusy?(busy: boolean): void;
}

const TIMEOUT_MS = 15_000;
const TICK_MS = 20_000;
/** 같은 기기 이어 쓰기 최소 간격(판마다 쓰기 → 서버 쓰기량 제한). 화면이 숨겨질 때는 바로 올린다 */
const MIN_PUSH_GAP_MS = 60_000;
const CHECK_GAP_MS = 5 * 60_000;
const MAX_BACKOFF_MS = 5 * 60_000;

async function call<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}, keepalive = false): Promise<{ status: number; body: T }> {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), TIMEOUT_MS) : 0;
  let res: Response;
  try {
    res = await fetch(path, {
      method, credentials: 'same-origin', keepalive, signal: ctl?.signal ?? undefined,
      headers: body === undefined ? headers : { 'content-type': 'application/json', ...headers },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch { throw new TransportError(0, 'network'); }
  finally { if (timer) clearTimeout(timer); }
  let parsed: unknown = null;
  try { parsed = await res.json(); } catch { /* 본문 없음 */ }
  // 409는 저장 충돌(conflict)만 본문으로 돌려준다. 그 밖의 409(already-linked·migration-busy 등)는 오류로 던진다 — 호출자가 상태를 바꾸기 전에(Codex P2 #214)
  const p0 = (parsed ?? {}) as { error?: string; retryAfterMs?: number };
  if (res.status >= 400 && !(res.status === 409 && p0.error === 'conflict')) {
    throw new TransportError(res.status, p0.error ?? 'http-' + res.status, p0.retryAfterMs);
  }
  return { status: res.status, body: parsed as T };
}

/** 계정 API 전송(동기화 전송 + 세션·Google·연결 해제·삭제) */
export type AccountApi = Transport & {
  session(): Promise<SessionResponse>; google(idToken: string): Promise<GoogleResponse>;
  unlink(): Promise<{ account: AccountView }>; remove(): Promise<void>;
};
/** 시험용 주입: 전송과 Google 로그인(기본은 fetch·GIS) */
export interface AccountClientDeps {
  api?: AccountApi; signIn?: () => Promise<string | null>;
  /**
   * 캐릭터 동기화(docs/CHARACTER-PRD.md §13.4): 지금 캐릭터가 있으면 진행 스냅샷은 계정 저장(/api/save) 대신 그 캐릭터 저장(/api/characters/:id/save)으로 맞춘다.
   * 기본은 src/characters/scope.ts의 지금 캐릭터 + fetch 전송. null이면 끔(계정 저장만 — 캐릭터 기능이 꺼진 지금 빌드와 같다)
   */
  character?: { id: string; transport: Transport; register: (accountId: string) => Promise<boolean> } | null;
  /** 기존 유저 이관(계정당 한 번, 서버 기준 — characters/online.ts). 기본은 캐릭터 기능이 켜져 있고 지금 캐릭터가 없을 때. null이면 끔 */
  migrate?: ((accountId: string) => Promise<OnlineMigrationResult>) | null;
}

/** fetch 전송(같은 출처 /api, 쿠키 자동). keepalive는 페이지가 숨겨질 때의 마지막 올리기용 */
export function fetchTransport(keepalive = () => false): AccountApi {
  return {
    async getSave(metaOnly = false) { return (await call<SaveResponse>('GET', metaOnly ? '/api/save?meta=1' : '/api/save')).body; },
    async putSave(rev: number, data: Snapshot, overwrite = false) {
      const body = { data };
      // keepalive 본문은 64KB 상한: 넘으면 일반 요청(페이지가 닫히면 다음 접속에 다시 올린다)
      const ka = keepalive() && JSON.stringify(body).length < 60_000;
      const headers: Record<string, string> = { 'if-match': `"${rev}"` };
      if (overwrite) headers['x-dh-sync'] = 'overwrite';
      const r = await call<PutResponse | ConflictResponse>('PUT', '/api/save', body, headers, ka);
      return r.status === 409 ? { ok: false as const, conflict: r.body as ConflictResponse } : { ok: true as const, res: r.body as PutResponse };
    },
    async session() { return (await call<SessionResponse>('POST', '/api/account/session', {})).body; },
    async google(idToken: string) { return (await call<GoogleResponse>('POST', '/api/account/google', { idToken })).body; },
    async unlink() { return (await call<{ account: AccountView }>('POST', '/api/account/unlink', {})).body; },
    async remove() { await call('DELETE', '/api/account', { confirm: 'delete' }); },
  };
}

function safeStorage(): KV | null {
  try { const s = window.localStorage; s.getItem('dungeonhero.account.v1'); return s; } catch { return null; }
}

export class AccountClient {
  status: AccountStatus = { state: 'connecting', provider: null, accountId: null, lastSyncAt: 0, note: '' };
  private readonly listeners = new Set<(s: AccountStatus) => void>();
  private readonly storage: KV | null;
  private readonly engine: SyncEngine | null;
  /** 지금 캐릭터의 스냅샷 동기화(없으면 계정 저장 동기화) */
  private readonly charEngine: SyncEngine | null;
  private readonly registerCharacter: ((accountId: string) => Promise<boolean>) | null;
  private readonly charId: string | null;
  private readonly migrate: ((accountId: string) => Promise<OnlineMigrationResult>) | null;
  private hiding = false;
  private readonly api: AccountApi;
  private readonly signIn: () => Promise<string | null>;
  private hooks: AccountHooks | null = null;
  private busy = false;
  private started = false;
  private sessionOk = false;
  private lastPushTry = 0;
  private lastCheck = 0;
  private backoffMs = 0;
  private backoffUntil = 0;
  private pendingPull = false;
  private timer = 0;

  constructor(storage: KV | null = safeStorage(), deps: AccountClientDeps = {}) {
    this.storage = storage;
    this.api = deps.api ?? fetchTransport(() => this.hiding);
    this.signIn = deps.signIn ?? googleSignIn;
    this.engine = storage ? new SyncEngine(storage, this.api) : null;
    const charId = activeCharacterId();
    const ch = deps.character !== undefined ? deps.character
      : storage && charId ? { id: charId, transport: characterTransport(charId, () => this.hiding), register: (accountId: string) => ensureRegistered(storage, charId, undefined, accountId) } : null;
    this.charEngine = storage && ch ? new SyncEngine(new CharacterStorage(storage, ch.id), ch.transport, () => Date.now(), charSyncKeys(ch.id), true) : null;
    this.registerCharacter = ch ? ch.register : null;
    this.charId = ch ? ch.id : null;
    this.migrate = deps.migrate !== undefined ? deps.migrate
      : storage && !ch && charactersEnabled() ? (accountId: string) => runOnlineMigration(storage, accountId, { locks: browserLocks(), api: fetchCharacterApi(), transport: (cid) => characterTransport(cid) }) : null;
    if (this.engine) {
      const m = this.engine.meta;
      this.status = { state: m.optOut ? 'off' : m.needsRelink ? 'relink' : 'connecting', provider: m.provider, accountId: m.accountId, lastSyncAt: m.lastSyncAt, note: '' };
    } else this.status = { state: 'error', provider: null, accountId: null, lastSyncAt: 0, note: '이 브라우저는 저장소를 쓸 수 없어 서버 저장이 꺼져 있어요' };
  }

  onChange(f: (s: AccountStatus) => void): () => void { this.listeners.add(f); f(this.status); return () => this.listeners.delete(f); }
  private set(p: Partial<AccountStatus>): void {
    this.status = { ...this.status, ...p };
    for (const f of this.listeners) { try { f(this.status); } catch { /* UI 오류가 동기화를 멈추지 않게 */ } }
  }

  /** 진행 스냅샷을 맞추는 엔진(지금 캐릭터가 있으면 캐릭터, 없으면 계정) */
  private target(): SyncEngine | null { return this.charEngine ?? this.engine; }

  /** 다음 부팅에 보일 "불러옴" 알림을 한 번 꺼낸다 */
  takeRestoredNotice(): boolean {
    const t = this.target();
    if (!t || !t.meta.restoredNotice || !this.storage) return false;
    t.meta.restoredNotice = false;
    t.persist();
    return true;
  }

  /** 게임 연결만 붙인다(타이머·화면 이벤트 없음 — 시험용, start가 부른다) */
  attach(hooks: AccountHooks): void { this.hooks = hooks; }

  start(hooks: AccountHooks): void {
    // 이미 시작했으면 훅만 바꾼다(캐릭터 첫 실행 화면에서 시작한 뒤 「서버 없이 시작」으로 게임을 만들 때 — src/ui/characterEntry.ts)
    if (this.started) { this.attach(hooks); return; }
    if (!this.engine) return;
    this.started = true;
    this.attach(hooks);
    this.timer = window.setInterval(() => { void this.tick(); }, TICK_MS);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') { this.hiding = true; void this.run(false).finally(() => { this.hiding = false; }); }
      else if (Date.now() - this.lastCheck > 60_000) void this.run(true);
    });
    window.addEventListener('pagehide', () => { this.hiding = true; void this.run(false); });
    if (!this.paused()) void this.run(true);
  }

  /** 자동 동기화를 멈춘 상태(계정 삭제 뒤 로컬 유지 · 연결 계정 세션 소실) */
  private paused(): boolean { const m = this.engine?.meta; return !m || m.optOut || m.needsRelink; }

  private async tick(): Promise<void> {
    if (!this.engine || this.paused() || this.busy) return;
    if (document.visibilityState === 'hidden') return;
    const now = Date.now();
    if (now < this.backoffUntil) return;
    if (this.pendingActivate) { this.flushPendingActivate(); return; }
    if (this.pendingPull && this.hooks?.isSafe()) { void this.run(true); return; }
    const id = this.engine.meta.accountId;
    if (this.sessionOk && id && now - this.lastPushTry >= MIN_PUSH_GAP_MS && this.target()!.localChanged(id)) { void this.run(false); return; }
    if (now - this.lastCheck >= CHECK_GAP_MS) void this.run(true);
  }

  private async ensureSession(): Promise<string> {
    const e = this.engine!;
    if (this.sessionOk && e.meta.accountId) return e.meta.accountId;
    this.set({ state: 'connecting', note: '' });
    const s = await this.api.session();
    // 연결돼 있던 Google 계정이 아닌 다른 계정(새 게스트 등)이 돌아왔다(다른 기기에서 삭제 등): 채택해 Google 메타를 덮지 않고 재연결을 기다린다
    if (e.meta.provider === 'google' && e.meta.accountId && s.account.id !== e.meta.accountId) throw new TransportError(401, 'account-changed');
    this.adopt(s.account);
    this.sessionOk = true;
    return s.account.id;
  }

  private adopt(a: AccountView): void {
    const e = this.engine!;
    e.meta.accountId = a.id; e.meta.provider = a.provider;
    saveMeta(this.storage!, e.meta);
    this.set({ provider: a.provider, accountId: a.id });
  }

  /** 동기화 한 번(check = 서버 변경 확인 포함). 실패는 상태로만 */
  async run(check: boolean): Promise<void> {
    const e = this.engine;
    if (!e || this.paused() || this.busy) return;
    this.busy = true;
    try {
      const id = await this.ensureSession();
      this.set({ state: 'syncing' });
      this.lastPushTry = Date.now();
      if (check) this.lastCheck = Date.now();
      // 캐릭터: 설치가 끝나지 않은 캐릭터(표시가 남음)는 올리지 않는다 — 반쯤 쓰인 스냅샷이 서버를 덮지 않게(받기·목록 맞추기가 다시 설치)
      if (this.charEngine && this.storage && this.charId && isInstalling(this.storage, this.charId)) throw new TransportError(0, 'character-installing');
      // 캐릭터: 서버에 등록(소유 확인)된 뒤에만 스냅샷을 올린다
      if (this.charEngine && this.registerCharacter && !(await this.registerCharacter(id))) throw new TransportError(0, 'character-unregistered');
      // 이관 전(원래 키로 플레이 중)이고 안전 장면이면: 게임 저장을 먼저 원래 키에 쓰고 계정 저장 동기화로 원래 키 = 계정 서버 정본을 만든 뒤 이관(online.ts)
      // 이관(원래 키 모드 · 안전 장면에서만 시작): 시작부터 끝까지 플레이를 짧게 막고(setBusy), 끝난 뒤 활성화(새로고침)는 안전 장면에서만
      const migrating = !this.charEngine && this.migrate && !this.pendingActivate && this.hooks?.isSafe();
      if (migrating) { this.hooks?.setBusy?.(true); try { this.hooks?.flush(); } catch { /* 저장 실패면 지금 원래 키로 */ } }
      try {
        const out = await this.target()!.sync({ accountId: id, canPull: !!this.hooks?.isSafe(), check });
        this.backoffMs = 0;
        this.after(out);
        if (migrating && (out.kind === 'noop' || out.kind === 'pushed')) {
          this.set({ state: 'syncing', note: '캐릭터를 준비하는 중…' });
          const r = await this.migrate!(id);
          if (r === 'done') { this.activateWhenSafe(); return; }
          this.set({ state: 'synced', note: r === 'busy' ? '다른 기기에서 캐릭터를 준비하는 중이에요' : '' });
        }
      } finally { if (migrating && !this.pendingActivate) this.hooks?.setBusy?.(false); }
    } catch (err) {
      this.fail(err);
    } finally { this.busy = false; }
  }

  /** 이관 완료 뒤 캐릭터 활성화(새로고침): 안전 장면이면 바로, 아니면 안전 장면이 될 때까지 미룬다(판을 끊지 않게 — staged restore와 같다) */
  private pendingActivate = false;
  private activateWhenSafe(): void {
    if (this.hooks?.isSafe()) { this.pendingActivate = false; this.hooks.reload(); return; }
    this.pendingActivate = true;
    this.hooks?.setBusy?.(false);
    this.set({ state: 'pending', note: '마을로 돌아가면 캐릭터로 이어서 시작해요' });
  }
  /** 시험·화면 이벤트용: 미뤄 둔 활성화를 지금 다시 본다 */
  flushPendingActivate(): void { if (this.pendingActivate) this.activateWhenSafe(); }

  private after(out: SyncOutcome): void {
    const e = this.target()!;
    this.pendingPull = out.kind === 'deferred';
    switch (out.kind) {
      case 'staged':
        // 응답을 기다리는 사이 던전에 들어갔을 수 있다: 적용(새로고침) 직전에 안전 장면인지 다시 확인하고, 아니면 대기 사본을 버리고 다음 안전 시점에 다시 받는다
        if (!this.hooks?.isSafe()) {
          try { this.storage?.removeItem(e.keys.apply); } catch { /* 부팅 때 같은 스냅샷을 적용해도 사본이 남는다 */ }
          this.pendingPull = true;
          this.set({ state: 'pending', note: '마을로 돌아가면 다른 기기의 진행을 불러와요' });
          return;
        }
        this.set({ state: 'syncing', note: '다른 기기의 진행을 불러오는 중…' });
        try { this.hooks?.flush(); } catch { /* 저장 실패여도 적용 직전 사본이 남는다 */ }
        this.hooks?.reload();
        return;
      case 'deferred':
        this.set({ state: 'pending', note: '마을로 돌아가면 다른 기기의 진행을 불러와요' });
        return;
      case 'blocked':
        this.set({ state: 'error', note: e.meta.lastError ? '서버 진행을 이 기기에 쓰지 못했어요(저장 공간을 확인해 주세요)' : '잠시 뒤 다시 맞춰요' });
        return;
      case 'too-large':
        this.set({ state: 'too-large', note: '저장이 커서 서버에 보관하지 못했어요(게임은 그대로 저장돼요)' });
        return;
      case 'unavailable':
        this.set({ state: 'error', note: '이 브라우저 저장소를 읽을 수 없어요' });
        return;
      default:
        this.set({ state: 'synced', lastSyncAt: e.meta.lastSyncAt || Date.now(), note: out.kind === 'pushed' && out.clamped.length ? '일부 항목은 서버 상한을 넘어 이전 값으로 보관했어요' : '' });
    }
  }

  private fail(err: unknown): void {
    const t = err instanceof TransportError ? err : new TransportError(0, 'network');
    if (t.status === 401) {
      this.sessionOk = false;
      // Google 연결 계정의 세션이 사라졌다(다른 기기에서 계정 삭제 등): 새 게스트를 몰래 만들어 올리지 않고 사용자에게 맡긴다
      const m = this.engine?.meta;
      if (m && m.provider === 'google') {
        m.needsRelink = true; m.base = null; m.accountId = null; m.provider = null;
        saveMeta(this.storage!, m);
        this.set({ state: 'relink', provider: null, accountId: null, note: '계정 연결이 끊겼어요(다른 기기에서 삭제했을 수 있어요) · Google로 다시 연결하거나 서버 저장을 새로 켜 주세요' });
        return;
      }
    }
    const base = t.retryAfterMs ?? (this.backoffMs ? Math.min(MAX_BACKOFF_MS, this.backoffMs * 2) : 30_000);
    this.backoffMs = base;
    this.backoffUntil = Date.now() + base;
    const offline = t.status === 0 || t.status >= 500;
    this.set({ state: offline ? 'offline' : 'error', note: offline ? '서버에 연결하지 못했어요 · 이 기기에 계속 저장하고 다음 접속 때 맞춰요' : t.status === 429 ? '잠시 뒤 다시 맞춰요'
      : t.code === 'slots-full' ? '계정에 캐릭터 칸이 부족해 이 캐릭터는 이 기기에만 보관돼요' : '서버 저장 오류(' + t.code + ')' });
  }

  /** Google로 계정 연결(또는 그 Google의 계정으로 전환). 결과 문장(실패·취소 사유) 또는 null(성공) */
  async connectGoogle(): Promise<string | null> {
    if (!this.engine) return '이 브라우저에서는 계정을 연결할 수 없어요';
    // 중지 상태(optOut·needsRelink)는 연결이 성공할 때까지 그대로 둔다: 인증 대기 중에 타이머가 새 게스트를 만들어 올리지 못하게, 취소·실패하면 기존 상태 유지
    const wasPaused = this.engine.meta.optOut || this.engine.meta.needsRelink;
    const token = await this.signIn();
    if (!token) return '연결을 취소했어요';
    while (this.busy) await new Promise((r) => setTimeout(r, 100));
    this.busy = true;
    try {
      // 중지 상태에서는 세션 쿠키만 받고 메타에 채택하지 않는다(실패·취소 때 새 게스트가 남지 않게, 성공하면 연결된 계정을 채택)
      if (wasPaused) await this.api.session(); else await this.ensureSession();
      // 캐릭터: 병합·전환 전에 지금 계정으로 캐릭터 진행을 먼저 올린다(서버가 옮기는 스냅샷이 최신이도록 — 옮긴 뒤 옛 값을 다시 받지 않게)
      // (설치가 끝나지 않은 캐릭터는 올리지 않는다 — 반쯤 쓰인 스냅샷)
      if (this.charEngine && !wasPaused && this.engine.meta.accountId && !(this.storage && this.charId && isInstalling(this.storage, this.charId))) {
        try {
          if (this.registerCharacter) await this.registerCharacter(this.engine.meta.accountId);
          await this.charEngine.sync({ accountId: this.engine.meta.accountId, canPull: false, check: true });
        } catch { /* 올리지 못해도 연결은 진행(기준은 캐릭터 id + rev로 판정해 서버가 더 앞서면 받는다) */ }
      }
      const r = await this.api.google(token);
      // 응답을 검증한 뒤에만 중지 상태를 푼다(오류 본문이 성공처럼 오면 서버 저장을 끈 사용자가 게스트로 다시 저장하게 된다 — Codex P2 #214)
      if (!r || typeof r !== 'object' || !r.account || typeof r.account.id !== 'string' || (r.result !== 'linked' && r.result !== 'switched' && r.result !== 'already')) throw new TransportError(0, 'bad-response');
      this.engine.meta.optOut = false; this.engine.meta.needsRelink = false;
      this.adopt(r.account);
      this.sessionOk = true;
    } catch (err) {
      if (err instanceof TransportError && err.code === 'account-changed') this.fail(err);
      const code = err instanceof TransportError ? err.code : 'network';
      return code === 'migration-busy' ? '캐릭터를 준비하는 중이에요. 잠시 뒤 다시 연결해 주세요'
        : code === 'slots-full' ? '합치면 캐릭터가 최대 칸 수를 넘어 연결하지 못했어요. 캐릭터를 정리한 뒤 다시 연결해 주세요'
        : code === 'already-linked' ? '이미 다른 Google 계정이 연결돼 있어요. 연결을 해제한 뒤 다시 시도해 주세요'
        : code === 'bad-token' || code === 'token-expired' ? 'Google 로그인을 확인하지 못했어요. 다시 시도해 주세요'
        : code === 'link-disabled' || code === 'link-unavailable' ? '지금은 Google 연결을 쓸 수 없어요. 잠시 뒤 다시 시도해 주세요'
        : '연결하지 못했어요(' + code + ')';
    } finally { this.busy = false; }
    await this.run(true);
    return null;
  }

  async unlink(): Promise<string | null> {
    if (!this.engine || this.busy) return '잠시 뒤 다시 시도해 주세요';
    this.busy = true;
    try { const r = await this.api.unlink(); this.adopt(r.account); return null; }
    catch (err) { return '연결 해제에 실패했어요(' + (err instanceof TransportError ? err.code : 'network') + ')'; }
    finally { this.busy = false; }
  }

  /** 계정 삭제: 서버 데이터를 모두 지운다. wipeLocal이면 이 기기의 진행도 지우고 새로 시작, 아니면 서버 저장을 끈다(optOut) */
  async deleteAccount(wipeLocal: boolean): Promise<string | null> {
    if (!this.engine || !this.storage) return '이 브라우저에서는 삭제할 수 없어요';
    while (this.busy) await new Promise((r) => setTimeout(r, 100));
    this.busy = true;
    try {
      await this.ensureSession();
      await this.api.remove();
    } catch (err) {
      this.busy = false;
      return '삭제하지 못했어요(' + (err instanceof TransportError ? err.code : 'network') + ') · 다시 시도해 주세요';
    }
    this.sessionOk = false;
    const meta = freshMeta();
    if (wipeLocal) {
      // 지우기는 다음 부팅 맨 처음에 한다(지금 지우면 새로고침 직전 pagehide 저장이 메모리 진행을 다시 쓴다)
      saveMeta(this.storage, meta);
      this.engine.meta = meta;
      if (stageWipe(this.storage)) { this.hooks?.reload(); return null; }
      this.busy = false;
      this.set({ state: 'off', provider: null, accountId: null, lastSyncAt: 0, note: '' });
      return '서버 계정은 삭제했지만 이 기기의 진행은 지우지 못했어요(저장소 오류)';
    }
    meta.optOut = true;
    saveMeta(this.storage, meta);
    // 캐릭터: 기기에 남긴 캐릭터는 새 계정에서 일반 등록으로 올라가게 보존 표시(이관 claim이 없어도 되게)
    try { retainLocalCharacters(this.storage); } catch { /* 목록 없음 */ }
    this.engine.meta = meta;
    this.busy = false;
    this.set({ state: 'off', provider: null, accountId: null, lastSyncAt: 0, note: '계정을 삭제했어요 · 이 기기의 진행은 그대로예요' });
    return null;
  }

  /** 계정 삭제 뒤 서버 저장 다시 켜기(새 게스트 계정에 지금 진행을 올린다) */
  enable(): void {
    if (!this.engine || !this.storage) return;
    this.engine.meta.optOut = false;
    this.engine.meta.needsRelink = false;
    saveMeta(this.storage, this.engine.meta);
    this.set({ state: 'connecting', note: '' });
    void this.run(true);
  }

  stop(): void { if (this.timer) clearInterval(this.timer); }
}
